
<script lang="ts" setup>
// Packages
import dayjs from 'dayjs';
import type { IEvents } from '~/server/api/events/types';

// ---------------------------------------
// Composables
// ---------------------------------------
const { t } = useI18n();

const { data: allEvents } = await useAsyncData(
  'events',
  () => $fetch<IEvents>('/api/events'),
  {
    default: () => ({ docs: [], page: 1, limit: 10, totalDocs: 0, totalPages: 0, hasNextPage: false, hasPrevPage: false, nextPage: null, prevPage: null, pagingCounter: 1 }),
  }
);


// Grouped by month
const eventsByMonth = computed(() => allEvents.value.docs.reduce((acc, event) => {
  const month = dayjs(event.date).format('MM-YYYY');

  if (!acc[month]) {
    acc[month] = [];
  }

  acc[month].push(event);

  return acc;
}, {} as Record<string, Array<any>>));

// ---------------------------------------
// Meta
// ---------------------------------------

useHead(() => ({
  title: t('UPCOMING_EVENTS'),
}));
</script>

<template>
  <main class="inline-block h-full px-4 md:px-10">
    <header class="text-center mt-28 block mb-24">
      <span class="text-4xl md:text-6xl font-bold">
        {{ $t('UPCOMING_EVENTS') }}
      </span>
      
      <div class="block my-6">
        <nuxt-link to="/" class="hover:underline">
          {{ $t('HOME') }}
        </nuxt-link>  
        -
        <span>
          {{ $t('ALL_EVENTS') }}
        </span>
      </div>
    </header>

    <section class="py-8 mx-auto max-w-7xl gap-8 items-start">
      <div
        class="my-12 block"
        v-for="(events, month) in eventsByMonth"
        :key="month"
      >
        <!-- Section header -->
        <div class="flex items-center my-8 gap-6">
          <span class="text-xl font-bold uppercase">{{ $dayjs('01-' + month, 'DD-MM-YYYY', true).format('MMMM, YYYY') }}</span>
          <div class="flex-grow border-t border-gray-300"></div>
        </div>
        <ul class="flex flex-col">
          <EventsCardList
            v-for="event in events"
            :key="event.id"
            :date="event.date"
            :description="event.description"
            :title="event.title"
            :location="event.location"
            :image="event.image?.url"
            :start-at="event.date"
            :end-at="event.end_at"
          />
        </ul>
      </div>
    </section>
  </main>
</template>
<style lang="scss" scoped>
main {
  background-color: #fff;

  ul {
    li:not(:last-child) {
      margin-bottom: 32px;
    }
  }
}
</style>